import { getUnsafeChainIdToDomain } from '@sparkdotfi/common-universal'
import { map, partition, pipe, sumBy } from 'remeda'

import { AccountsNavigationProps, ShortAccountDefinition } from '../types'
import { SUPPORTED_CHAINS } from '@/config/chain/constants'
import { NativeAssetInfo } from '@/config/chain/types'
import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { SavingsAccount, SavingsAccountRepository } from '@/features/savings/logic/savings-converters/types'
import { InternalLink } from '@/routing/types'

export interface MakeNavigationPropsParams {
  chainId: number
  savingsAccounts: SavingsAccountRepository
  nativeAssetInfo: NativeAssetInfo
  selectedAccount: TokenSymbol
  tokenRepository: TokenRepository
}

export function makeNavigationProps({
  chainId,
  savingsAccounts,
  nativeAssetInfo,
  selectedAccount,
  tokenRepository,
}: MakeNavigationPropsParams): AccountsNavigationProps {
  function getShortAccountDefinition(savingsAccount: SavingsAccount): ShortAccountDefinition {
    const { savingsToken, underlyingToken, converter } = savingsAccount
    const savingsTokenBalance = tokenRepository.findOneBalanceOrZero(savingsToken.symbol)
    const underlyingTokenDeposit = converter.convertToAssets({ shares: savingsTokenBalance })
    const label = (() => {
      if (savingsToken.symbol === TokenSymbol('stUSDS')) {
        return 'Sky stUSDS'
      }
      if (underlyingToken.address === nativeAssetInfo.wrappedNativeAssetAddress) {
        return nativeAssetInfo.nativeAssetSymbol
      }
      return underlyingToken.symbol
    })()
    const apy = converter.apy
    const knownChainId = SUPPORTED_CHAINS.find((chain) => chain.id === chainId)?.id
    const domain = knownChainId ? getUnsafeChainIdToDomain(knownChainId) : 'mainnet'
    const link = InternalLink({
      to: '/savings/$domain/$savingsToken',
      params: { domain, savingsToken: savingsToken.symbol.toLowerCase() },
    })
    return { underlyingToken, underlyingTokenDeposit, vaultToken: savingsToken, label, apy, link }
  }

  const [regularSavingsAccounts, opportunityAccounts] = pipe(
    savingsAccounts.all(),
    map(getShortAccountDefinition),
    partition(({ vaultToken }) => vaultToken.symbol !== TokenSymbol('stUSDS')),
  )

  const hasSparkV2Accounts = savingsAccounts.all().some(({ savingsToken }) => savingsToken.symbol.startsWith('sp'))
  const [mainSavingsAccounts, legacyAccounts] = (() => {
    if (hasSparkV2Accounts) {
      return pipe(
        regularSavingsAccounts,
        partition(
          ({ vaultToken }) => vaultToken.symbol !== TokenSymbol('sUSDC') && vaultToken.symbol !== TokenSymbol('sDAI'),
        ),
      )
    }
    return [regularSavingsAccounts, []]
  })()

  const legacyAccountsExpanded =
    pipe(
      legacyAccounts,
      sumBy(({ underlyingTokenDeposit }) => underlyingTokenDeposit.toNumber()),
    ) > 0 || legacyAccounts.some(({ vaultToken }) => vaultToken.symbol === selectedAccount)

  return {
    savingsAccounts: mainSavingsAccounts,
    opportunityAccounts,
    legacyAccounts,
    legacyAccountsExpanded,
    selectedAccount,
  }
}
